import React from 'react';
import { Link } from 'react-router-dom';
import Button from '@components/Button/Button';
import { useSchool } from '@context/SchoolContext';
import { useOverviewContext } from '@context/OverviewContext';
import config from '../../config';
import attendIcon from '@assets/images/school_item.svg';
import olsIcon from '@assets/images/computer_school_item.svg';
import coachIcon from '@assets/images/backpack_school_item.svg';
import clockIcon from '@assets/icons/coming-soon-alarm.png';

interface SchoolInformationProps {
  eyebrow?: string;
  header: string;
  description?: string | React.ReactNode;
  theme?: 'transparent' | 'white' | 'secondary-light';
  faqCtaUrl?: string;
}

const SchoolInformation: React.FC<SchoolInformationProps> = ({
  eyebrow,
  header,
  description,
  theme = 'secondary-light',
  faqCtaUrl,
}) => {

  const { userRoleVal,  students, loggedInUser = null, loading, error } = useOverviewContext();
  const { NAVIGATE_OLS, LEARNING_ROLE_COACH, ABOUT_ATTENDING_SCHOOL } = config.app.routes;
  const { data: schoolData } = useSchool();
  const isSchoolInfoReady = !!schoolData?.onboarding_content?.school_info_ready;
  const schoolAbbreviation = schoolData?.abbreviation || '';

  const isStudent = userRoleVal === 'STUDENT';

  const schoolInfoItems = [
    ...(isSchoolInfoReady
      ? [
        {
          title: `What to Know About Attending ${schoolAbbreviation}`,
          icon: <img src={attendIcon} alt="Attend" />,
          linkUrl: ABOUT_ATTENDING_SCHOOL,
        }
      ]
      : []),
    {
      title: 'Navigating the K12 School (OLS)',
      icon: <img src={olsIcon} alt="OLS" />,
      linkUrl: NAVIGATE_OLS,
    },

    ...(!isStudent
      ? [
          {
          title: 'The Learning Coach Role',
          icon: <img src={coachIcon} alt="Coach" />,
          linkUrl: LEARNING_ROLE_COACH,
        },
      ]
      : []),
  ];

  const blockClasses = [
    'school-info-block',
    `school-info-block--${theme}`,
    !isSchoolInfoReady ? 'coming-soon-module' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={blockClasses}>
      <div className="school-info-block__left">
        <div className="school-info-block__titleblock">
          {eyebrow && <div className="school-info-block__eyebrow">{eyebrow}</div>}
          <h2 className="school-info-block__header">{header} {schoolAbbreviation}</h2>
        </div>
        <p className="school-info-block__description">{description}</p>
      </div>

      <div
        className={`school-info-block__right ${
          !isSchoolInfoReady ? 'coming-soon-block' : ''
        }`}
      >
      {!isSchoolInfoReady && (
        <div className="school-info-block__svg-wrapper">
          <svg width="596" height="110" viewBox="0 0 596 110" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g opacity="0.5">
              <path fillRule="evenodd" clipRule="evenodd" d="M577.919 78.3892C473.43 106.726 355.81 49.6609 290.431 56.6554C241.65 61.8742 185.312 132.191 66.1234 102.88C-37.3885 77.4237 -11.4895 64.584 -26.081 12.2468C-40.2839 -38.6967 35.8252 -59.8244 101.773 -97.7842C168.411 -93.1349 286.277 -34.6419 374.525 -41.2188C501.116 -50.6533 781.572 23.1588 577.919 78.3892Z" fill="#EAEAEA"/>
            </g>
          </svg>
        </div>
        )}

        {isSchoolInfoReady ? (
          schoolInfoItems.map((item, idx) => (
            <Link
              to={item.linkUrl}
              key={idx}
              className="school-info-block__item school-info-block__item--link"
            >
              <span className="school-info-block__item-title">{item.title}</span>
              <span className="school-info-block__item-icon">{item.icon}</span>
            </Link>
          ))
        ) : (
          <>
            <div className="coming-soon-icon">
              <img src={clockIcon} alt="Coming Soon" />
            </div>

            <div className="coming-soon-content">
              <strong className="school-info-block__coming-title">Coming soon</strong>
              <p className="school-info-block__coming-description">
                We're working on gathering information for you! Check this space again closer
                to the beginning of the school year.
              </p>
            </div>

            <div className="coming-soon-footer">
              <strong className="school-info-block__tag">Wondering something?</strong>
              {faqCtaUrl && (
                <Button
                  label="Read the FAQs"
                  size="lg"
                  variant="border"
                  width="fit-content"
                  color="primary"
                  breakpoint="all"
                  className="school-info-block__faq-link"
                  linkUrl={ faqCtaUrl }
                  linkTarget="_self"
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SchoolInformation;
